import { action, observable } from "mobx";

import { sharePendingPromise } from "@libs/decorators/sharePendingPromise.ts";
import {
  BhbAppointmentTypeDto,
  BhbAppointmentTypeSortOrderDto,
  BhbLocationDto,
  BhbProviderDto,
  PatchBhbLocationDto
} from "@libs/gateways/bhb/bhbGateway.dtos.ts";
import { IBhbGateway } from "@libs/gateways/bhb/bhbGateway.interface.ts";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import type { IRootStore } from "@shared-types/root/root-store.interface.ts";
import { permission } from "@stores/decorators/permission.ts";
import type { Store } from "@stores/types/store.type.ts";
import { mergeModel } from "@stores/utils/store.utils.ts";

import { BhbRef } from "./BhbRef.ts";
import { BhbAppointmentType } from "./models/BhbAppointmentType.ts";
import { BhbLocation } from "./models/BhbLocation.ts";
import { BhbProvider } from "./models/BhbProvider.ts";

export class BhbStore implements Store<BhbStore, BhbRef> {
  constructor(private gateway: IBhbGateway) {
    this.ref = new BhbRef(this.gateway);
  }

  root: IRootStore;
  private get notification() {
    return this.root.notification;
  }

  ref: BhbRef;

  locationMap = observable.map<string, BhbLocation>();
  locationAppointmentTypesMap = observable.map<string, BhbAppointmentType>();
  providersMap = observable.map<string, BhbProvider>();

  //Appt types
  @action
  private mergeApptType = (dto: BhbAppointmentTypeDto, locationId: string) => {
    return mergeModel({
      dto,
      getNewModel: () => new BhbAppointmentType(dto),
      map: this.locationAppointmentTypesMap,
      id: dto.id + locationId
    });
  };

  // This is a temporary method to return an appointment type until we do future work
  //  to allow different appointment types for each location
  getAppointmentTypes() {
    // all appointment types are the same for now so we just use a default location ID
    return this.getAppointmentTypesForLocation(this.root.core.locationId);
  }

  @permission([Permission.BhbRead])
  async getAppointmentTypesForLocation(locationId: string) {
    const apptTypes =
      await this.gateway.getAppointmentTypesForLocation(locationId);

    return apptTypes.map(dto => this.mergeApptType(dto, locationId));
  }

  // This is a temporary method to get all appointment types
  //  until we do future work to allow appointment types to differ by location
  async getAppointmentType(appointmentTypeId: string) {
    const allAppointmentTypes = Array.from(
      this.locationAppointmentTypesMap.values()
    );

    const apptType = allAppointmentTypes.find(
      appointmentType => appointmentType.id === appointmentTypeId
    );

    if (apptType) {
      return apptType;
    }

    return this.getAppointmentTypeForLocation(
      appointmentTypeId,
      this.root.core.locationId // all appointment types are the same for now so we just use a default location ID
    );
  }

  @permission([Permission.BhbRead])
  private async getAppointmentTypeForLocation(
    appointmentTypeId: string,
    locationId: string
  ): Promise<BhbAppointmentType | undefined> {
    const appointmentType = this.locationAppointmentTypesMap.get(
      appointmentTypeId + locationId
    );
    if (appointmentType) return appointmentType;

    const result = await this.gateway.getAppointmentTypeForLocation(
      locationId,
      appointmentTypeId
    );

    return this.mergeApptType(result, locationId);
  }

  // This is a temporary method to bulk update all appointment types
  //  until we do future work to allow appointment types to differ by location
  async updateAppointmentType(
    appointmentType: Omit<BhbAppointmentTypeDto, "eTag">
  ) {
    await this.root.practice
      .getAllOrgUnitsLocationData()
      .then(orgUnits =>
        Promise.all(
          orgUnits.map(orgUnit =>
            this.putAppointmentTypeForLocation(appointmentType, orgUnit.id)
          )
        )
      );
    return this.getAppointmentType(appointmentType.id);
  }

  async putAppointmentTypeForLocation(
    appointmentType: Omit<BhbAppointmentTypeDto, "eTag">,
    locationId: string
  ) {
    const existingAppointmentType = await this.getAppointmentTypeForLocation(
      appointmentType.id,
      locationId
    );
    if (!existingAppointmentType) {
      throw new Error("No appointment type for this location");
    }

    const updated = await this.gateway.putAppointmentTypes(locationId, {
      ...appointmentType,
      eTag: existingAppointmentType.eTag
    });

    return this.mergeApptType(updated, locationId);
  }

  // This is a temporary method to modify the sort order for each location
  //  until we allow different sort orders for each location
  async updateAppointmentTypeSortOrder(
    sortRequest: BhbAppointmentTypeSortOrderDto[]
  ) {
    const orgUnits = await this.root.practice.getAllOrgUnitsLocationData();
    const results = await Promise.all(
      orgUnits.map(orgUnit =>
        this.updateAppointmentTypeSortOrderForLocation(sortRequest, orgUnit.id)
      )
    );

    return results.find(x => !!x);
  }

  private async updateAppointmentTypeSortOrderForLocation(
    sortRequest: BhbAppointmentTypeSortOrderDto[],
    locationId: string
  ) {
    const updated = await this.gateway.updateSortOrder(locationId, sortRequest);

    return updated.map(dto => this.mergeApptType(dto, locationId));
  }

  //Location
  @action
  private mergeLocation = (dto: BhbLocationDto) => {
    return mergeModel({
      dto,
      getNewModel: () => new BhbLocation(dto),
      map: this.locationMap
    });
  };

  @sharePendingPromise()
  async getLocation(locationId: string): Promise<BhbLocation> {
    const cachedLocation = this.locationMap.get(locationId);
    if (cachedLocation) {
      return cachedLocation;
    }

    const location = await this.gateway.getLocation(locationId);
    return this.mergeLocation(location);
  }

  patchLocation(location: PatchBhbLocationDto) {
    return this.gateway.patchLocation(location).then(this.mergeLocation);
  }

  @action
  private mergeProvider = (dto: BhbProviderDto) => {
    return mergeModel({
      dto,
      getNewModel: () => new BhbProvider(this.root, dto),
      map: this.providersMap,
      id: dto.id + dto.locationId // id is the provider ID which won't be unique if there is multiple locations
    });
  };

  async getProvidersForLocation(locationId: string): Promise<BhbProvider[]> {
    const result = await this.gateway.getProvidersForLocation(locationId);
    // we add the locationId onto the dto as otherwise there is no way to differentiate two dtos for the same provider
    return result.map(provider =>
      this.mergeProvider({ ...provider, locationId })
    );
  }

  async getProvidersForUser(providerId: string): Promise<BhbProvider[]> {
    const result = await this.gateway.getProviderLocations(providerId);
    // we add the providerId onto the dto as otherwise there is no way to differentiate two dtos for the same provider
    return result.map(provider =>
      this.mergeProvider({ ...provider, id: providerId })
    );
  }

  async updateProvider(
    provider: BhbProviderDto
  ): Promise<BhbProvider | undefined> {
    const result = await this.gateway.updateProviderForLocation(provider);

    const model = this.mergeProvider({
      ...result,
      locationId: provider.locationId
    });

    return model;
  }

  async uploadPhoto(locationId: string, providerId: string, file: File) {
    return this.gateway.uploadProviderPhoto(locationId, providerId, file);
  }

  // This is a temporary method to bulk update the logo for each location
  //  until we do further work to allow a separate logo for each location
  async uploadLogo(file: File): Promise<string> {
    const orgUnits = await this.root.practice.getAllOrgUnitsLocationData();
    const results = await Promise.all(
      orgUnits.map(orgUnit => this.gateway.uploadLogo(orgUnit.id, file))
    );

    return results[0];
  }

  @permission([Permission.BhbWrite])
  uploadLogoForLocation(file: File, locationId: string): Promise<string> {
    return this.gateway.uploadLogo(locationId, file);
  }
}
